import type { GuidedEditCreativeApi } from "@common/interfaces/adsManagerGuidedEdit";
import {
  creativeBlockHasPartnerPost,
  guidedWireHasPartnerPost,
} from "@common/utils/instagramPartnershipCreative";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  partnerInstagramFieldsFromLinkedFacebookPage,
  partnerFacebookFieldsFromLinkedInstagramAccount,
  resolvePartnerFacebookPageById,
  resolvePartnerIdsForGuidedWire,
  resolvePartnerInstagramAccountByWire,
} from "./resolveCreativePartnerDisplay";

export { resolvePartnerIdsForGuidedWire } from "./resolveCreativePartnerDisplay";

/** Form {@link CreativeBlock} → guided-edit API wire (snake_case partner fields). */
export function mapCreativeBlockToGuidedCreative(
  creative: CreativeBlock,
  options?: { partnerPostsEnabled?: boolean }
): GuidedEditCreativeApi {
  const partnerPostsEnabled = options?.partnerPostsEnabled ?? false;
  const wire: GuidedEditCreativeApi = {
    name: creative.name,
    primary_text: creative.primaryText.trim(),
    ...(creative.squareMediaUrl ? { square_url: creative.squareMediaUrl } : {}),
    ...(creative.squareMediaFileKey
      ? { square_file_key: creative.squareMediaFileKey }
      : {}),
    ...(creative.squareMediaType
      ? { square_media_type: creative.squareMediaType }
      : {}),
    ...(creative.verticalMediaUrl
      ? { portrait_url: creative.verticalMediaUrl }
      : {}),
    ...(creative.verticalMediaFileKey
      ? { portrait_file_key: creative.verticalMediaFileKey }
      : {}),
    ...(creative.verticalMediaType
      ? { portrait_media_type: creative.verticalMediaType }
      : {}),
    fb_page_id: creative.fbPage?.id ?? "",
    instagram_id: creative.instagramAccount?.id ?? "",
    link: creative.link?.trim() ?? "",
  };

  const partnerIds = resolvePartnerIdsForGuidedWire(creative);
  const hasPartnerPost =
    creativeBlockHasPartnerPost({
      ...creative,
      partnerFacebookPageId: partnerIds.partnerFacebookPageId,
      partnerInstagramUserId: partnerIds.partnerInstagramUserId,
      partnerInstagramUsername: partnerIds.partnerInstagramUsername,
    });

  if (hasPartnerPost && partnerPostsEnabled) {
    wire.partner_post_enabled = true;
    const partnerFacebookPage = creative.partnerFacebookPage;
    const partnerInstagramAccount = creative.partnerInstagramAccount;
    if (partnerIds.partnerFacebookPageId) {
      wire.partner_facebook_page_id = partnerIds.partnerFacebookPageId;
    }
    if (partnerIds.partnerInstagramUserId) {
      wire.partner_instagram_user_id = partnerIds.partnerInstagramUserId;
    }
    if (partnerIds.partnerInstagramUsername) {
      wire.partner_instagram_username = partnerIds.partnerInstagramUsername;
    }
    const linkedInstagramUserId =
      partnerFacebookPage?.linked_instagram_user_id?.trim();
    if (partnerIds.partnerFacebookPageId && linkedInstagramUserId) {
      wire.partner_facebook_linked_instagram_user_id = linkedInstagramUserId;
    }
    const permissionStatus =
      (partnerFacebookPage?.partnership_permission_status as
        | CreativeBlock["partnerPermissionStatus"]
        | undefined) ?? creative.partnerPermissionStatus;
    if (permissionStatus) {
      wire.partner_permission_status = permissionStatus;
    }
  } else {
    wire.partner_post_enabled = false;
  }

  return wire;
}

const CLEAR_PARTNER_FIELDS: Pick<
  CreativeBlock,
  | "partnerPostEnabled"
  | "partnerInstagramUserId"
  | "partnerInstagramUsername"
  | "partnerPermissionStatus"
  | "partnerFacebookPageId"
  | "partnerFacebookPage"
  | "partnerInstagramAccount"
> = {
  partnerPostEnabled: false,
  partnerInstagramUserId: undefined,
  partnerInstagramUsername: undefined,
  partnerPermissionStatus: undefined,
  partnerFacebookPageId: undefined,
  partnerFacebookPage: null,
  partnerInstagramAccount: null,
};

/** Guided-edit GET wire → form {@link CreativeBlock} partner fields (identity mapped elsewhere). */
export function partnerFieldsFromGuidedWire(
  c: GuidedEditCreativeApi
): Pick<
  CreativeBlock,
  | "partnerPostEnabled"
  | "partnerInstagramUserId"
  | "partnerInstagramUsername"
  | "partnerPermissionStatus"
  | "partnerFacebookPageId"
> {
  if (!guidedWireHasPartnerPost(c)) {
    return { partnerPostEnabled: false };
  }
  const partnerInstagramUserId =
    c.partner_instagram_user_id?.trim() ||
    c.partner_facebook_linked_instagram_user_id?.trim() ||
    undefined;
  return {
    partnerPostEnabled: true,
    partnerInstagramUserId,
    partnerInstagramUsername: c.partner_instagram_username,
    partnerPermissionStatus: c.partner_permission_status,
    partnerFacebookPageId: c.partner_facebook_page_id,
  };
}

/** Wire partner ids + resolved page/account objects for form and review display. */
export function hydratePartnerFieldsFromGuidedWire(
  c: GuidedEditCreativeApi,
  pages: readonly FacebookPage[],
  instagramAccounts: readonly InstagramAccount[]
): Pick<
  CreativeBlock,
  | "partnerPostEnabled"
  | "partnerInstagramUserId"
  | "partnerInstagramUsername"
  | "partnerPermissionStatus"
  | "partnerFacebookPageId"
  | "partnerFacebookPage"
  | "partnerInstagramAccount"
> {
  const fields = partnerFieldsFromGuidedWire(c);
  if (!fields.partnerPostEnabled) {
    return CLEAR_PARTNER_FIELDS;
  }

  const partnerFacebookPageFromWire =
    resolvePartnerFacebookPageById(fields.partnerFacebookPageId, pages) ??
    null;
  const linkedFromPage = partnerFacebookPageFromWire
    ? partnerInstagramFieldsFromLinkedFacebookPage(
        partnerFacebookPageFromWire,
        instagramAccounts
      )
    : null;

  const partnerInstagramUserId =
    fields.partnerInstagramUserId?.trim() ||
    linkedFromPage?.partnerInstagramUserId?.trim() ||
    undefined;
  const partnerInstagramUsername =
    fields.partnerInstagramUsername?.trim() ||
    linkedFromPage?.partnerInstagramUsername?.trim() ||
    undefined;

  const partnerInstagramAccount =
    resolvePartnerInstagramAccountByWire(
      partnerInstagramUserId,
      partnerInstagramUsername,
      instagramAccounts
    ) ??
    linkedFromPage?.partnerInstagramAccount ??
    null;

  const linkedFromInstagram =
    !partnerFacebookPageFromWire && partnerInstagramAccount
      ? partnerFacebookFieldsFromLinkedInstagramAccount(
          partnerInstagramAccount,
          pages
        )
      : null;
  const partnerFacebookPage =
    partnerFacebookPageFromWire ?? linkedFromInstagram?.partnerFacebookPage ?? null;

  return {
    ...fields,
    partnerInstagramUserId,
    partnerInstagramUsername,
    partnerFacebookPageId:
      partnerFacebookPage?.id ?? fields.partnerFacebookPageId,
    partnerFacebookPage,
    partnerInstagramAccount,
  };
}

export { CLEAR_PARTNER_FIELDS };
